import { useEffect, useState } from "react";
import { X, SealCheck, Crown, Sparkle, Eye } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

const TIER_META = {
  observador: {
    icon: Eye,
    perks: [
      "Acceso al feed privado",
      "Notificación de nuevos releases",
      "Badge de apoyo en tu perfil",
    ],
  },
  coleccionista: {
    icon: SealCheck,
    perks: [
      "Todo lo anterior",
      "Descuento 15% en tienda",
      "Obra digital exclusiva mensual",
      "Acceso anticipado a drops",
    ],
  },
  mecenas: {
    icon: Crown,
    perks: [
      "Todo lo anterior",
      "1 llamada mensual 1:1",
      "Tu nombre en créditos del próximo proyecto",
      "Edición física firmada al año",
    ],
  },
};

export default function SubscribeDrawer({ artist, onClose }) {
  const [pricing, setPricing] = useState(null);
  const [tier, setTier] = useState("coleccionista");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/pricing");
        setPricing(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  const tiers = pricing?.subscription_tiers || {
    observador: { amount: 3, label: "Observador" },
    coleccionista: { amount: 9, label: "Coleccionista" },
    mecenas: { amount: 25, label: "Mecenas" },
  };

  const subscribe = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/payments/subscriptions/checkout", {
        handle: artist.handle,
        tier,
        origin: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="subscribe-drawer"
      className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full sm:max-w-2xl bg-[#050505] border border-[#27272A] max-h-[92vh] overflow-auto">
        <header className="sticky top-0 bg-[#050505] border-b border-[#27272A] p-5 flex items-center justify-between z-10">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
              // SUSCRIBIRSE A
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
              @{artist.handle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
          >
            <X size={14} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          <p className="font-mono text-[12px] text-[#A1A1AA] tracking-wider leading-relaxed">
            Acceso mensual a contenido exclusivo de{" "}
            <span className="text-white">{artist.name}</span>. Se renueva cada
            mes manualmente — sin cargos automáticos.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(tiers).map(([key, t]) => {
              const Meta = TIER_META[key] || TIER_META.observador;
              const Icon = Meta.icon;
              const active = tier === key;
              return (
                <button
                  key={key}
                  data-testid={`sub-tier-${key}`}
                  onClick={() => setTier(key)}
                  className={`text-left p-4 border transition-colors ${
                    active
                      ? "border-[#FF3E00] bg-[#FF3E00]/5"
                      : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
                  }`}
                >
                  <Icon
                    size={22}
                    weight={active ? "fill" : "regular"}
                    className={active ? "text-[#FF3E00]" : "text-[#A1A1AA]"}
                  />
                  <div className="font-display font-black uppercase tracking-tight text-xl mt-3">
                    {t.label}
                  </div>
                  <div className="font-display font-black text-4xl text-[#FF3E00] mt-1 leading-none">
                    ${t.amount}
                    <span className="font-mono text-[10px] tracking-[0.22em] text-[#A1A1AA]">
                      {" "}
                      /mes
                    </span>
                  </div>
                  <ul className="mt-4 space-y-1.5 font-mono text-[11px] text-[#A1A1AA] tracking-wider">
                    {Meta.perks.map((p, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#FF3E00]">→</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          {error ? (
            <div className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899]">
              {error}
            </div>
          ) : null}

          <button
            data-testid="sub-subscribe"
            onClick={subscribe}
            disabled={loading}
            className={`btn-brutal w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-xs tracking-[0.22em] font-bold py-4 ${
              loading
                ? "bg-[#121212] text-[#525252]"
                : "bg-[#FF3E00] text-white"
            }`}
          >
            <Sparkle size={14} weight="fill" />
            {loading ? "Redirigiendo a Stripe…" : `Pagar $${tiers[tier]?.amount} con Stripe`}
          </button>

          <p className="font-mono text-[9px] text-[#525252] tracking-[0.22em] uppercase text-center">
            · Pago seguro · Stripe · 30 días de acceso por pago ·
          </p>
        </div>
      </div>
    </div>
  );
}
